import { Button, Flex, FormControl } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import { AddIcon } from "@chakra-ui/icons";
import {
	type User,
	UserCenterAccess,
	UserCenterRole,
} from "../../types/user.types";
import { useCounter } from "@uidotdev/usehooks";
import { forwardRef, useImperativeHandle } from "react";
import InputDoubleSelect from "../inputs/InputDoubleSelect";
import InputSelect from "../inputs/InputSelect";

type InviteUserFormProps = {
	/**
	 * Predefined fields. If defined related inputs should not be displayed.
	 */
	role?: UserCenterRole;
};

export type InviteUserFormHandler = {
	values: () => Partial<User>[];
};

const InviteUserForm = forwardRef<InviteUserFormHandler, InviteUserFormProps>(
	({ role }, ref) => {
		const methods = useForm();
		const [count, { increment }] = useCounter(1, { min: 1 });

		useImperativeHandle(ref, () => ({
			values: () => {
				const rawValues = methods.getValues();
				const values = Array(count)
					.fill(null)
					.map((_, idx) => {
						if (role === undefined) {
							if (
								rawValues[`email${idx}`] &&
								rawValues[`email${idx}-select-a`] &&
								rawValues[`email${idx}-select-b`]
							) {
								return {
									email: rawValues[`email${idx}`],
									role: rawValues[`email${idx}-select-a`],
									access: rawValues[`email${idx}-select-b`],
								};
							}
						}
						if (role !== undefined) {
							if (rawValues[`email${idx}`] && rawValues[`email${idx}-select`]) {
								return {
									email: rawValues[`email${idx}`],
									role: role,
									access: rawValues[`email${idx}-select`],
								};
							}
						}
						return null;
					})
					.filter((e) => e !== null);
				return values;
			},
		}));
		return (
			<Flex w="100%">
				<FormProvider {...methods}>
					<form style={{ marginTop: "32px", width: "100%" }}>
						<Flex gap={3} direction={"column"}>
							{Array(count)
								.fill(0)
								.map((_, index) => (
									<FormControl key={index}>
										{role === undefined && (
											<InputDoubleSelect
												id={`email${index}`}
												placeholder="Adresse email"
												{...methods.register(`email${index}`)}
												optionsA={[
													{
														value: UserCenterRole.AUDIOPROSTHESIST,
														label: "Audioprothésiste",
													},
													{
														value: UserCenterRole.ASSISTANT,
														label: "Assistant(e)",
													},
													{ value: UserCenterRole.OTHER, label: "Autre" },
												]}
												optionsB={[
													{ value: UserCenterAccess.MEMBER, label: "Membre" },
													{
														value: UserCenterAccess.ADMIN,
														label: "Administrateur",
													},
												]}
											></InputDoubleSelect>
										)}
										{role !== undefined && (
											<InputSelect
												id={`email${index}`}
												placeholder="Adresse email"
												{...methods.register(`email${index}`)}
												options={[
													{ value: UserCenterAccess.MEMBER, label: "Membre" },
													{
														value: UserCenterAccess.ADMIN,
														label: "Administrateur",
													},
												]}
											></InputSelect>
										)}
									</FormControl>
								))}
						</Flex>

						<Flex w="100%" justifyContent="end" my={5}>
							<Button onClick={() => increment()}>
								<AddIcon mr={3}></AddIcon>
								Ajouter un utilisateur
							</Button>
						</Flex>
					</form>
				</FormProvider>
			</Flex>
		);
	},
);

export default InviteUserForm;
